import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plane, Key, Upload, Trash2, Copy, Clock, MapPin, Gauge, ArrowUpDown, FileText, Plus, Activity, Link2, BarChart3, AlertCircle, CheckCircle2, ExternalLink, QrCode, X, FileDown, Map as MapIcon, Satellite } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { projectsAPI } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const FlightDataPage = () => {
  const { user, token, isSuperAdmin } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManage = isSuperAdmin || isAdmin;

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [tokens, setTokens] = useState([]);
  const [qrToken, setQrToken] = useState(null);
  const [flights, setFlights] = useState([]);
  const [stats, setStats] = useState(null);
  const [showNewToken, setShowNewToken] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('flights'); // flights | tokens | upload
  const [newFlightIds, setNewFlightIds] = useState([]);  // For pulse animation
  const [liveConnected, setLiveConnected] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.list();
        setProjects(res.data);
        if (res.data.length > 0) {
          const userP = res.data.find(p => p.id === user?.project_id);
          setSelectedProject(userP ? userP.id : res.data[0].id);
        }
      } catch (e) { console.error(e); }
    };
    fetchProjects();
  }, [user?.project_id]);

  const fetchFlights = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const [fRes, sRes] = await Promise.all([
        fetch(`${API}/api/flight-data/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/flight-data-stats/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (fRes.ok) setFlights(await fRes.json());
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) { console.error(e); }
  }, [selectedProject, token]);

  const fetchTokens = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/flight-tokens`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTokens(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchFlights(); }, [fetchFlights]);
  useEffect(() => { if (canManage) fetchTokens(); }, [canManage, fetchTokens]);

  // WebSocket for real-time flight-sync notifications
  useEffect(() => {
    if (!selectedProject) return;
    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsBase = (API || window.location.origin).replace(/^https?:/, wsProto);
    const ws = new WebSocket(`${wsBase}/api/ws/flight-sync?project_id=${encodeURIComponent(selectedProject)}`);
    ws.onopen = () => setLiveConnected(true);
    ws.onclose = () => setLiveConnected(false);
    ws.onerror = () => setLiveConnected(false);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.event === 'flight:new' && msg.flight?.project_id === selectedProject) {
          // Prepend flight and mark for pulse animation
          setFlights((prev) => [msg.flight, ...prev]);
          setNewFlightIds((prev) => [...prev, msg.flight.id]);
          toast.success(`Novo voo sincronizado: ${msg.flight.drone_model || 'Drone'} - ${msg.flight.pilot_name || 'piloto'}`);
          // Clear pulse after animation ends
          setTimeout(() => {
            setNewFlightIds((prev) => prev.filter((id) => id !== msg.flight.id));
          }, 4000);
        }
      } catch { /* ignore non-JSON */ }
    };
    // Keepalive ping every 30s
    const pingInt = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30000);
    return () => {
      clearInterval(pingInt);
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [selectedProject]);

  const createToken = async () => {
    if (!selectedProject || !tokenName.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/flight-tokens`, {
        method: 'POST', headers,
        body: JSON.stringify({ project_id: selectedProject, name: tokenName.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedToken(data);
        setTokenName('');
        fetchTokens();
        toast.success('Token criado!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao criar token');
      }
    } catch { toast.error('Erro de conexao'); }
    setLoading(false);
  };

  const revokeToken = async (tokenId) => {
    if (!confirm('Revogar este token? Apps usando-o perderao acesso.')) return;
    try {
      const res = await fetch(`${API}/api/flight-tokens/${tokenId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { fetchTokens(); toast.success('Token revogado'); }
    } catch { toast.error('Erro'); }
  };

  const deleteFlight = async (flightId) => {
    if (!confirm('Excluir este registro de voo?')) return;
    try {
      const res = await fetch(`${API}/api/flight-data/${flightId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { fetchFlights(); toast.success('Registro excluido'); }
    } catch { toast.error('Erro'); }
  };

  const uploadLog = async () => {
    if (!uploadFile || !selectedProject) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('project_id', selectedProject);
    try {
      const res = await fetch(`${API}/api/flight-data/upload-log`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setUploadFile(null);
        fetchFlights();
      } else {
        toast.error(data.detail || 'Erro ao processar arquivo');
      }
    } catch { toast.error('Erro de conexao'); }
    setLoading(false);
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado!'); };

  const formatDuration = (sec) => {
    if (!sec) return '0s';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.round(sec % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const downloadFile = async (path, fname) => {
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Falha ao baixar');
    }
  };

  const pwaUrl = `${window.location.origin}/pwa-flight`;
  const flightSyncUrl = `${window.location.origin}/flightsync`;
  const projectTokens = tokens.filter(t => t.project_id === selectedProject);

  return (
    <div className="space-y-6" data-testid="flight-data-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Plane size={20} className="text-gold" /> DADOS DE VOO
          <span
            className={`inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-full ${liveConnected ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}
            data-testid="flight-data-live-indicator"
            title={liveConnected ? 'WebSocket conectado - atualizacao em tempo real ativa' : 'Sem conexao tempo real'}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${liveConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
            {liveConnected ? 'LIVE' : 'OFFLINE'}
          </span>
        </h2>
        <div className="flex items-center gap-3">
          {(isSuperAdmin || projects.length > 1) && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-[200px] input-hud text-xs h-8" data-testid="flight-project-select">
                <SelectValue placeholder="Projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="flight-stats">
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Total Voos</p>
            <p className="text-2xl font-rajdhani font-bold text-navy">{stats.total_flights}</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Horas Voadas</p>
            <p className="text-2xl font-rajdhani font-bold text-blue-600">{stats.total_duration_hours}h</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Distancia (km)</p>
            <p className="text-2xl font-rajdhani font-bold text-green-600">{stats.total_distance_km}</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Alt. Max (m)</p>
            <p className="text-2xl font-rajdhani font-bold text-amber-600">{stats.max_altitude_meters || 0}</p>
          </div>
          <div className="card-hud p-4">
            <p className="text-gray-500 text-[10px] font-mono uppercase">Tokens Ativos</p>
            <p className="text-2xl font-rajdhani font-bold text-gold">{projectTokens.length}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-1">
        {[
          { key: 'flights', label: 'REGISTROS DE VOO', icon: Activity },
          ...(canManage ? [{ key: 'tokens', label: 'TOKENS', icon: Key }] : []),
          { key: 'upload', label: 'UPLOAD LOG', icon: Upload },
          ...(canManage ? [{ key: 'pwa', label: 'APP CONTROLE', icon: ExternalLink }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-rajdhani font-semibold uppercase tracking-wider transition-colors rounded-t-lg ${
              tab === t.key ? 'text-gold border-b-2 border-gold bg-gold/5' : 'text-gray-400 hover:text-navy'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Flights */}
      {tab === 'flights' && (
        <div className="card-hud overflow-hidden" data-testid="flights-table">
          {flights.length === 0 ? (
            <div className="p-8 text-center">
              <Plane size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">Nenhum dado de voo registrado</p>
              <p className="text-gray-300 text-xs mt-1">Use o app PWA no controle ou faca upload de logs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Data</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Drone</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Piloto</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Duracao</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Distancia</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Alt. Max</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-[10px] uppercase">Origem</th>
                    {canManage && <th className="text-right p-3 text-gray-500 font-mono text-[10px] uppercase">Acoes</th>}
                  </tr>
                </thead>
                <tbody>
                  {flights.map((f, idx) => {
                    const isNew = newFlightIds.includes(f.id);
                    return (
                    <tr key={f.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isNew ? 'bg-green-50 animate-pulse' : ''}`}
                      style={isNew ? { boxShadow: 'inset 3px 0 0 0 #10b981' } : undefined}
                      data-testid={`flight-row-${idx}`}
                    >
                      <td className="p-3 text-xs text-navy font-medium">{formatDate(f.start_time)}</td>
                      <td className="p-3 text-xs text-gray-600">{f.drone_serial || f.drone_model || '-'}</td>
                      <td className="p-3 text-xs text-gray-600">{f.pilot_name || '-'}</td>
                      <td className="p-3 text-xs font-mono text-blue-600">{formatDuration(f.duration_seconds)}</td>
                      <td className="p-3 text-xs font-mono text-green-600">{f.distance_meters > 1000 ? `${(f.distance_meters / 1000).toFixed(1)}km` : `${Math.round(f.distance_meters)}m`}</td>
                      <td className="p-3 text-xs font-mono text-amber-600">{f.max_altitude_meters ? `${f.max_altitude_meters}m` : '-'}</td>
                      <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${f.source === 'pwa_app' ? 'bg-gold/10 text-gold' : f.source === 'csv_upload' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>{f.source}</span>
                        {(f.rtk_summary?.has_rtk || f.rtk_summary?.has_rtk_photos) && (
                          <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full font-mono bg-purple-100 text-purple-700" title={`Acuracia H ${((f.rtk_summary.avg_h_acc_m || f.rtk_summary.mrk_avg_h_acc_m || 0) * 100).toFixed(1)}cm`}>
                            <Satellite size={9} className="inline mr-0.5" /> RTK {f.rtk_summary.fix_pct ?? f.rtk_summary.mrk_fix_pct ?? 0}%
                          </span>
                        )}
                      </td>
                      {canManage && (
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-600"
                              title="Baixar relatorio PDF"
                              onClick={() => downloadFile(`/api/rtk/${f.id}/report.pdf`, `rtk_report_${f.id.slice(0,8)}.pdf`)}
                              data-testid={`download-pdf-${idx}`}
                            >
                              <FileDown size={12} />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-emerald-600"
                              title="Baixar trajetoria KML"
                              onClick={() => downloadFile(`/api/rtk/${f.id}/track.kml`, `track_${f.id.slice(0,8)}.kml`)}
                              data-testid={`download-kml-${idx}`}
                            >
                              <MapIcon size={12} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-300 hover:text-red-500" onClick={() => deleteFlight(f.id)} data-testid={`delete-flight-${idx}`}>
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Tokens */}
      {tab === 'tokens' && canManage && (
        <div className="space-y-4">
          <div className="card-hud p-4 flex items-center justify-between">
            <div>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">Tokens de Projeto</p>
              <p className="text-gray-400 text-xs">Gere tokens para autenticar o envio de dados do controle remoto</p>
            </div>
            <Button onClick={() => setShowNewToken(true)} className="bg-gold hover:bg-gold/90 text-navy text-xs h-8" data-testid="new-token-btn">
              <Plus size={14} className="mr-1" /> NOVO TOKEN
            </Button>
          </div>

          {createdToken && (
            <div className="card-hud p-4 border-2 border-gold/30 bg-gold/5" data-testid="created-token-display">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-gold mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-navy font-semibold text-sm mb-1">Token criado: {createdToken.name}</p>
                  <p className="text-gray-400 text-[10px] mb-2">Copie agora. Este token nao sera exibido novamente.</p>
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-2">
                    <code className="text-xs font-mono text-navy flex-1 break-all" data-testid="token-value">{createdToken.token}</code>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gold hover:bg-gold/10 flex-shrink-0" onClick={() => copyToClipboard(createdToken.token)} data-testid="copy-token-btn">
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card-hud overflow-hidden">
            {projectTokens.length === 0 ? (
              <div className="p-8 text-center">
                <Key size={32} className="text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">Nenhum token para este projeto</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {projectTokens.map((t, idx) => (
                  <div key={t.id} className="flex items-center justify-between p-4 hover:bg-gray-50" data-testid={`token-row-${idx}`}>
                    <div>
                      <p className="text-navy font-medium text-sm flex items-center gap-2">
                        <Key size={12} className="text-gold" /> {t.name}
                      </p>
                      <p className="text-gray-400 text-[10px] font-mono mt-0.5">
                        <span className="text-navy font-semibold tracking-widest">{t.token}</span>
                        {' | '}Criado: {formatDate(t.created_at)} | Usos: {t.usage_count}
                        {t.last_used && ` | Ultimo: ${formatDate(t.last_used)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="text-cyan-600 hover:text-cyan-700 text-xs" onClick={() => setQrToken(t)} data-testid={`qr-token-${idx}`} title="Mostrar QR Code para o piloto">
                        <QrCode size={14} className="mr-1" /> QR
                      </Button>
                      <Button variant="ghost" size="sm" className="text-gold hover:text-navy text-xs" onClick={() => copyToClipboard(t.token)} data-testid={`copy-token-inline-${idx}`} title="Copiar token">
                        <Copy size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-gray-300 hover:text-red-500 text-xs" onClick={() => revokeToken(t.id)} data-testid={`revoke-token-${idx}`}>
                        <Trash2 size={12} className="mr-1" /> Revogar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Upload */}
      {tab === 'upload' && (
        <div className="card-hud p-6 space-y-4" data-testid="upload-log-section">
          <div className="flex items-start gap-3">
            <Upload size={20} className="text-gold mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">Upload de Log de Telemetria</p>
              <p className="text-gray-400 text-xs">Carregue arquivos .csv ou .txt de telemetria DJI para extrair dados de voo automaticamente</p>
            </div>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gold/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById('log-file-input').click()}>
            <input id="log-file-input" type="file" accept=".csv,.txt" className="hidden"
              onChange={(e) => setUploadFile(e.target.files[0])} data-testid="log-file-input" />
            <FileText size={32} className="text-gray-300 mx-auto mb-2" />
            {uploadFile ? (
              <p className="text-navy font-medium text-sm">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)</p>
            ) : (
              <p className="text-gray-400 text-sm">Clique para selecionar arquivo .csv ou .txt</p>
            )}
          </div>
          {uploadFile && (
            <Button onClick={uploadLog} disabled={loading} className="bg-gold hover:bg-gold/90 text-navy w-full h-10" data-testid="upload-log-btn">
              {loading ? 'Processando...' : 'PROCESSAR E IMPORTAR'}
            </Button>
          )}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-navy text-xs font-semibold flex items-center gap-1"><AlertCircle size={12} className="text-gold" /> Formatos suportados:</p>
            <ul className="text-gray-500 text-[11px] space-y-1 ml-4">
              <li>CSV com colunas de tempo, distancia, altitude, velocidade</li>
              <li>TXT com logs de telemetria DJI (timestamps, duracao, distancia)</li>
              <li>Logs de voo exportados do DJI Go / DJI Fly / Autel Explorer</li>
            </ul>
          </div>
        </div>
      )}

      {/* Tab: PWA App */}
      {tab === 'pwa' && canManage && (
        <div className="card-hud p-6 space-y-4" data-testid="pwa-section">
          <div className="flex items-start gap-3">
            <Plane size={20} className="text-gold mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">Apps para Controle Remoto (PWA)</p>
              <p className="text-gray-400 text-xs">Escolha o app adequado ao fluxo do piloto. Abra o link no navegador do controle (DJI RC Pro, Autel Smart Controller) ou do celular/tablet do piloto.</p>
            </div>
          </div>

          {/* FlightSync - NEW */}
          <div className="rounded-xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-50 to-purple-50 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono bg-cyan-500 text-white px-2 py-0.5 rounded-full">NOVO</span>
              <p className="text-navy font-rajdhani font-semibold text-sm uppercase">FlightSync - Importacao de Log</p>
            </div>
            <p className="text-gray-600 text-xs">Piloto importa o arquivo .csv/.txt do DJI Fly. O app extrai GPS, altitude, bateria, velocidade e sincroniza automaticamente. Funciona offline (fila local).</p>
            <div className="flex items-center gap-2 bg-white/70 rounded-lg border border-cyan-200 p-2.5">
              <Link2 size={14} className="text-cyan-600 flex-shrink-0" />
              <code className="text-xs font-mono text-navy flex-1 break-all">{flightSyncUrl}</code>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-cyan-600" onClick={() => copyToClipboard(flightSyncUrl)} data-testid="copy-flightsync-url">
                <Copy size={12} />
              </Button>
            </div>
            <Button onClick={() => window.open(flightSyncUrl, '_blank')} className="bg-cyan-500 hover:bg-cyan-600 text-white w-full h-9 text-xs" data-testid="open-flightsync-btn">
              <ExternalLink size={12} className="mr-2" /> ABRIR FLIGHTSYNC
            </Button>
          </div>

          {/* Legacy PWA */}
          <div className="rounded-xl border border-gray-200 p-4 space-y-3">
            <p className="text-navy font-rajdhani font-semibold text-sm uppercase">PWA Manual - Timer de Voo</p>
            <p className="text-gray-500 text-xs">Piloto anota manualmente inicio/fim do voo com timer. Ideal para registro rapido sem log.</p>
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5">
              <Link2 size={14} className="text-gold flex-shrink-0" />
              <code className="text-xs font-mono text-navy flex-1 break-all">{pwaUrl}</code>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-gold" onClick={() => copyToClipboard(pwaUrl)} data-testid="copy-pwa-url">
                <Copy size={12} />
              </Button>
            </div>
            <Button onClick={() => window.open(pwaUrl, '_blank')} variant="outline" className="w-full h-9 text-xs border-gold text-gold hover:bg-gold/10" data-testid="open-pwa-btn">
              <ExternalLink size={12} className="mr-2" /> ABRIR PWA MANUAL
            </Button>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-navy text-xs font-semibold">Fluxo recomendado:</p>
            <ol className="text-gray-500 text-[11px] space-y-1.5 ml-4 list-decimal">
              <li>Gere um token do projeto na aba TOKENS</li>
              <li>Envie o link do FlightSync + token ao piloto</li>
              <li>Piloto cola o token uma unica vez e comeca a importar logs</li>
              <li>Voos aparecem nesta pagina em tempo real (indicador <strong>LIVE</strong>)</li>
            </ol>
          </div>
        </div>
      )}

      {/* New Token Dialog */}
      <Dialog open={showNewToken} onOpenChange={setShowNewToken}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani">Novo Token de Projeto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-gray-500 text-xs font-mono uppercase block mb-1">Nome do Token</label>
              <Input value={tokenName} onChange={e => setTokenName(e.target.value)} placeholder="Ex: Controle DJI RC Pro - Piloto Joao" className="input-hud" data-testid="token-name-input" />
            </div>
            <div>
              <label className="text-gray-500 text-xs font-mono uppercase block mb-1">Projeto</label>
              <p className="text-navy font-medium text-sm">{projects.find(p => p.id === selectedProject)?.name || '-'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewToken(false)}>Cancelar</Button>
            <Button onClick={() => { createToken(); setShowNewToken(false); }} disabled={!tokenName.trim() || loading} className="bg-gold hover:bg-gold/90 text-navy" data-testid="confirm-create-token">Criar Token</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code modal - pilot scans to auto-configure FlightSync */}
      <Dialog open={!!qrToken} onOpenChange={(v) => !v && setQrToken(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy">
              <QrCode size={18} className="text-cyan-600" /> QR Code do Token
            </DialogTitle>
          </DialogHeader>
          {qrToken && (
            <div className="space-y-4" data-testid="qr-modal-content">
              <p className="text-gray-500 text-xs">
                Peça para o piloto <strong>escanear o QR code</strong> com a câmera do controle/celular. O FlightSync abre configurado automaticamente.
              </p>
              <div className="flex justify-center bg-white p-4 rounded-xl border border-gray-200">
                <QRCodeSVG
                  value={`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/flightsync?token=${encodeURIComponent(qrToken.token)}`}
                  size={220}
                  level="M"
                  imageSettings={{
                    src: '/logo192.png',
                    width: 32,
                    height: 32,
                    excavate: true,
                  }}
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <p className="text-[10px] font-mono uppercase text-gray-500 tracking-wider">TOKEN</p>
                <p className="text-navy text-lg font-mono font-bold text-center tracking-widest" data-testid="qr-token-text">{qrToken.token}</p>
                <p className="text-[10px] text-gray-400 font-mono break-all text-center">{process.env.REACT_APP_BACKEND_URL || window.location.origin}/flightsync?token={qrToken.token}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 h-9 text-xs" onClick={() => copyToClipboard(qrToken.token)} data-testid="qr-copy-token">
                  <Copy size={12} className="mr-1" /> COPIAR TOKEN
                </Button>
                <Button variant="outline" className="flex-1 h-9 text-xs" onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/flightsync?token=${qrToken.token}`)} data-testid="qr-copy-url">
                  <Link2 size={12} className="mr-1" /> COPIAR LINK
                </Button>
              </div>
              <Button onClick={() => setQrToken(null)} className="w-full bg-gold hover:bg-gold/90 text-navy h-9" data-testid="qr-close">
                <X size={14} className="mr-1" /> FECHAR
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlightDataPage;
